// Location utility functions for managing user location data
import { sessionHandler } from "../components/functions/sessionHandler";
import { createLogger } from "../utils/logger";

const logger = createLogger("LocationUtils");

const LOCATION_STORAGE_KEY = "pani_user_location";

const LOCATION_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

// Save location to session storage with timestamp
export const saveLocationToSession = (locationData) => {
  try {
    const data = {
      ...locationData,
      timestamp: Date.now()
    };
    sessionHandler(LOCATION_STORAGE_KEY, JSON.stringify(data), "set");
    logger.log("📍 Location saved to session:", locationData);
  } catch (error) {
    logger.error("Error saving location to session:", error);
  }
};

// Get location from session storage (if not expired)
export const getLocationFromSession = () => {
  try {
    const stored = sessionHandler(LOCATION_STORAGE_KEY, null, "get");
    if (!stored) return null;

    const data = JSON.parse(stored);
    const now = Date.now();

    // Check if location data is still valid (not expired)
    if (data.timestamp && now - data.timestamp < LOCATION_EXPIRY_MS) {
      logger.log("📍 Location retrieved from session:", data);
      return {
        address: data.address,
        coordinates: data.coordinates,
        lat: data.coordinates?.lat,
        lng: data.coordinates?.lng
      };
    } else {
      // Location expired, clear it
      clearLocationFromSession();
      return null;
    }
  } catch (error) {
    logger.error("Error reading location from session:", error);
    return null;
  }
};

// Clear location from session storage
export const clearLocationFromSession = () => {
  try {
    sessionHandler(LOCATION_STORAGE_KEY, null, "delete");
    logger.log("📍 Location cleared from session");
  } catch (error) {
    logger.error("Error clearing location from session:", error);
  }
};

// Extract location from URL parameters
export const getLocationFromURL = () => {
  try {
    // Safely access window.location
    if (typeof window === 'undefined' || !window.location) {
      return null;
    }
    
    const urlParams = new URLSearchParams(window.location.search);
    const lat = urlParams.get("lat");
    const lng = urlParams.get("lng");
    const address = urlParams.get("address");

    if (lat && lng) {
      const coordinates = {
        lat: parseFloat(lat),
        lng: parseFloat(lng)
      };

      return {
        address: address ? decodeURIComponent(address) : null,
        coordinates,
        lat: coordinates.lat,
        lng: coordinates.lng,
        source: "url"
      };
    }
    return null;
  } catch (error) {
    logger.error("Error extracting location from URL:", error);
    return null;
  }
};

// Get location from URL first, then session storage as fallback
export const getLocationData = () => {
  // First try URL parameters
  const urlLocation = getLocationFromURL();
  if (urlLocation) {
    // Save to session storage for future use
    saveLocationToSession(urlLocation);
    return urlLocation;
  }

  // Fallback to session storage
  const sessionLocation = getLocationFromSession();
  return sessionLocation;
};

// Update URL with location parameters
export const updateURLWithLocation = (address, coordinates, date) => {
  try {
    // Safely access window.location and window.history
    if (typeof window === 'undefined' || !window.location || !window.history) {
      logger.warn("window.location or window.history not available");
      return;
    }
    
    const params = new URLSearchParams();

    if (date) params.set("date", date);
    if (address) params.set("address", encodeURIComponent(address));
    if (coordinates?.lat) params.set("lat", coordinates.lat.toString());
    if (coordinates?.lng) params.set("lng", coordinates.lng.toString());

    const newURL = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, "", newURL);

    // Also save to session storage
    saveLocationToSession({ address, coordinates });

    logger.log("📍 URL updated with location:", newURL);
  } catch (error) {
    logger.error("Error updating URL with location:", error);
  }
};
